import { post, put, patch, del } from 'aws-amplify/api';
import { isCdkProxyConfigured } from '../lib/appConfig';
import { fetchCdkProxy, splitProxyUrl } from '../lib/cdkProxy';
import { auditCallerHeaders } from './auditCallerHeaders';

const METHODS = {
  POST: post,
  PUT: put,
  PATCH: patch,
  DELETE: del,
};

function manageBaseUrl() {
  const root = String(process.env.REACT_APP_NTVA_FLOW || '').replace(/\/$/, '');
  return root ? `${root}/latest/manage` : '';
}

function tryParseJsonText(text) {
  if (text == null || text === '') return null;
  const trimmed = String(text).trim();
  try {
    return JSON.parse(trimmed);
  } catch {
    // El proxy a veces entrega el JSON en base64.
  }
  try {
    if (typeof atob !== 'function') return null;
    return JSON.parse(atob(trimmed));
  } catch {
    return null;
  }
}

export async function readAmplifyJson(bodyResponse) {
  if (bodyResponse == null) return {};
  if (
    typeof bodyResponse === 'object' &&
    typeof bodyResponse.json !== 'function' &&
    typeof bodyResponse.text !== 'function'
  ) {
    return bodyResponse;
  }

  if (typeof bodyResponse.text === 'function') {
    try {
      const text = await bodyResponse.text();
      const parsed = tryParseJsonText(text);
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      // Si text() no existe o ya se consumió, caemos a json().
    }
  }

  if (typeof bodyResponse.json === 'function') {
    try {
      const parsed = await bodyResponse.json();
      if (typeof parsed === 'string') return tryParseJsonText(parsed) || {};
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      return {};
    }
  }

  return {};
}

export function createdResourceId(payload, keys = ['flowId', 'flow_id', 'voiceFlowId', 'id']) {
  if (!payload || typeof payload !== 'object') return '';
  for (const key of keys) {
    if (payload[key]) return String(payload[key]);
  }
  return '';
}

export function ntvaHubSpotIdFrom(path, body) {
  if (body && typeof body === 'object') {
    if (body.clientId) return String(body.clientId);
    if (body.hubspot_id) return String(body.hubspot_id);
    if (body.hubSpotId) return String(body.hubSpotId);
  }
  const { queryParams } = splitProxyUrl(path);
  if (queryParams?.clientId) return String(queryParams.clientId);
  const m = String(path || '').match(
    /\/(?:flow|voice_flow|tool|knowledge_base|mcp|survey|hubspot_property|entitlement|voice|voice_runtime_config|voice_provider_options|node_catalog|voice_conversation|reports\/chat|reports\/voice|client_limit|audit_evaluation|auditor|chat)\/([^/?]+)/,
  );
  return m ? decodeURIComponent(m[1]) : '';
}

/** Catálogo global de voces en NTVA. El CDK no acepta este valor como tenant. */
export const NTVA_GLOBAL_CLIENT_ID = '__global__';

/** Tenant para la URL del CDK: ignora `__global__` y cualquier id con guion/guion bajo. */
export function ntvaCdkHubSpotId(...candidates) {
  for (const raw of candidates) {
    const id = String(raw || '').trim();
    if (id && id !== NTVA_GLOBAL_CLIENT_ID && /^[A-Za-z0-9]{1,32}$/.test(id)) {
      return id;
    }
  }
  return '';
}

/**
 * GET/POST de manage o consumption por el CDK. Si no hay flag o no hay
 * hubSpotId (path, body o override), corre `direct`. Con el flag activo
 * no hay segundo intento. El override sirve para rutas sin tenant
 * (`/plan`): el CDK exige `{hubSpotId}` en la URL; NTVA no lo ve.
 */
export async function fetchNtvaViaCdkOrDirect({
  prefix,
  method,
  path,
  body,
  hubSpotId: hubSpotIdOverride,
  direct,
}) {
  const hubSpotId = ntvaCdkHubSpotId(hubSpotIdOverride, ntvaHubSpotIdFrom(path, body));
  if (isCdkProxyConfigured() && hubSpotId) {
    const { path: cleanPath, queryParams } = splitProxyUrl(path);
    const prefixed = `${prefix}${cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`}`;
    return fetchCdkProxy({
      service: 'ntva',
      hubSpotId,
      path: prefixed,
      method,
      body,
      queryParams,
    });
  }
  return direct();
}

async function directNtvaManage(method, path, body) {
  const base = manageBaseUrl();
  const apiKey = process.env.REACT_APP_NTVA_FLOW_API_KEY || '';
  if (!base) throw new Error('Falta REACT_APP_NTVA_FLOW');

  const response = await fetch(`${base}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(apiKey ? { 'x-api-key': apiKey } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`Respuesta no JSON (HTTP ${response.status}).`);
  }

  if (!response.ok) {
    const message = data?.message || data?.error || `HTTP ${response.status}`;
    throw new Error(message);
  }
  return data;
}

/**
 * Guarda por el proxy (CDK o Amplify `/proxy/ntva-flow`) para auditar quién.
 * Con `REACT_APP_NUA_PROXY_URL` solo CDK. Sin flag, Amplify; si Amplify
 * falla, cae al NTVA directo (mismo fetch de siempre).
 */
export async function proxyNtvaFlowManage(method, path, body, hubSpotIdOverride) {
  if (isCdkProxyConfigured()) {
    const managePath = `/latest/manage${path.startsWith('/') ? path : `/${path}`}`;
    return fetchCdkProxy({
      service: 'ntva',
      hubSpotId: ntvaCdkHubSpotId(hubSpotIdOverride, body?.clientId),
      path: managePath,
      method,
      body,
    });
  }

  const apiMethod = METHODS[String(method || '').toUpperCase()];
  if (!apiMethod) throw new Error(`Método no soportado: ${method}`);

  const options = {
    headers: {
      'Content-Type': 'application/json',
      ...(await auditCallerHeaders()),
    },
  };
  if (body !== undefined) options.body = body;

  try {
    const { body: bodyResponse } = await apiMethod({
      apiName: 'nuatalker',
      path: `/proxy/ntva-flow/latest/manage${path}`,
      options,
    }).response;
    return await readAmplifyJson(bodyResponse);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      '[auditoria] proxy ntva-flow no disponible, se guarda directo en NTVA',
      err?.message || err,
    );
    return directNtvaManage(method, path, body);
  }
}
